import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ObjectCountByTagPipe } from './object-count-by-tag.pipe';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces compartidas entre galería y heatmap
// ─────────────────────────────────────────────────────────────────────────────

export interface RecognitionBbox {
  x_min: number; y_min: number;
  x_max: number; y_max: number;
  width: number; height: number;
}

export interface RecognitionAttribute {
  attribute_type_name: 'company' | 'brand' | 'category' | 'size'
                     | 'package' | 'line' | 'flavor' | 'shape' | string;
  attribute_name: string;
}

export interface RecognitionObject {
  name:             string;
  tag:              string;
  score:            number;
  bbox:             RecognitionBbox;
  libraryObjectId?: number;
  attributes?:      RecognitionAttribute[];
}

export interface RecognitionRow {
  imageUrl: string;
  objects:  RecognitionObject[];
}

export interface RecognitionVisualizationConfig {
  title: string;
  rows:  RecognitionRow[];
}

// ─────────────────────────────────────────────────────────────────────────────

const TAG_COLORS: Record<string, string> = {
  postobon:    '#6366f1',
  competencia: '#f43f5e',
  precios:     '#f59e0b',
  nevera:      '#14b8a6',
};

const tagColor = (tag: string) => TAG_COLORS[tag] ?? '#94a3b8';

// ─────────────────────────────────────────────────────────────────────────────

interface GalleryRow extends RecognitionRow {
  imgW: number;
  imgH: number;
}

@Component({
  selector: 'app-bbox-gallery-card',
  standalone: true,
  imports: [CommonModule, ObjectCountByTagPipe],
  templateUrl: './bbox-gallery-card.component.html',
  styleUrl:    './bbox-gallery-card.component.scss',
})
export class BboxGalleryCardComponent implements OnChanges {
  @Input() config!: RecognitionVisualizationConfig;
  @Input() loading = false;

  activeTag = 'all';
  tags: { key: string; label: string; color: string }[] = [];

  /** Filas enriquecidas con dimensiones de imagen */
  rows: GalleryRow[] = [];

  /** Fila seleccionada para la vista ampliada */
  selectedRow: GalleryRow | null = null;

  ngOnChanges(): void { this.build(); }

  private build(): void {
    if (!this.config?.rows?.length) { this.rows = []; return; }

    // Tags presentes
    const tagKeys = [...new Set(this.config.rows.flatMap(r => r.objects.map(o => o.tag)))];
    this.tags = [
      { key: 'all', label: 'Todos los tags', color: '#94a3b8' },
      ...tagKeys
        .filter(t => TAG_COLORS[t])
        .map(t => ({ key: t, label: t.charAt(0).toUpperCase() + t.slice(1), color: TAG_COLORS[t] })),
    ];

    // Inicializar filas con dims = 0 (se actualizan al cargar la imagen)
    this.rows = this.config.rows.map(r => ({ ...r, imgW: 0, imgH: 0 }));
  }

  // ── Eventos de imagen ─────────────────────────────────────────────────────

  onImgLoad(event: Event, row: GalleryRow): void {
    const img = event.target as HTMLImageElement;
    row.imgW = img.naturalWidth  || 1;
    row.imgH = img.naturalHeight || 1;
  }

  onImgError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  // ── Filtros ───────────────────────────────────────────────────────────────

  setTag(key: string): void { this.activeTag = key; }

  visibleObjects(row: GalleryRow): RecognitionObject[] {
    return this.activeTag === 'all'
      ? row.objects
      : row.objects.filter(o => o.tag === this.activeTag);
  }

  // ── Posición CSS del bbox (%) ─────────────────────────────────────────────

  bboxStyle(obj: RecognitionObject, row: GalleryRow): Record<string, string> {
    if (!row.imgW || !row.imgH) return { display: 'none' };
    const l = (obj.bbox.x_min / row.imgW * 100).toFixed(2);
    const t = (obj.bbox.y_min / row.imgH * 100).toFixed(2);
    const w = (obj.bbox.width  / row.imgW * 100).toFixed(2);
    const h = (obj.bbox.height / row.imgH * 100).toFixed(2);
    const c = tagColor(obj.tag);
    return {
      left:    `${l}%`,
      top:     `${t}%`,
      width:   `${w}%`,
      height:  `${h}%`,
      border:  `2px solid ${c}`,
      '--bbox-bg': c,
    };
  }

  bboxLabelStyle(obj: RecognitionObject): Record<string, string> {
    return { background: tagColor(obj.tag) };
  }

  // ── Modal detalle ─────────────────────────────────────────────────────────

  openDetail(row: GalleryRow): void  { this.selectedRow = row; }
  closeDetail(): void                { this.selectedRow = null; }

  // ── Helpers ───────────────────────────────────────────────────────────────

  scorePct(s: number): string { return `${Math.round(s * 100)}%`; }
  tagColor(tag: string): string { return tagColor(tag); }

  trackByUrl(_: number, row: RecognitionRow): string { return row.imageUrl; }
  trackByIdx(i: number): number { return i; }
}
